import React, { useState } from 'react';

import classNames from 'classnames';
import AddIcon from 'icons/add.svg';
import PersonIcon from 'icons/person-lg.svg';
import SearchOffIcon from 'icons/search-off.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import { useInfiniteProjectsAssignablePersonnelSearchesQuery } from 'components/hooks/projectsAssignablePersonnelSearchQueries';
import useDebounce from 'components/hooks/useDebounce';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import Checkbox from 'components/application/Checkbox';
import ConditionalWrapper from 'components/application/ConditionalWrapper';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';
import SearchField from 'components/application/SearchField';
import Tooltip from 'components/application/Tooltip';
import TruncatableText from 'components/application/TruncatableText';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import AddPersonnelSidePanel from 'components/projectPersonnel/components/AddPersonnelSidePanel';
import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';

import { sortRolesByPrimaryAndPosition } from 'components/helpers/personnel';
import { personDisplayName } from 'components/helpers/users';

function buildSearchParams({ personnel, roleIds, teamIds }) {
  if (personnel.size === 0) return { per: 8 };

  return {
    filter: {
      ids: { in: [...personnel.keys()] },
      ...(roleIds.size > 0 && { company_role_ids: { in: [...roleIds] } }),
      ...(teamIds.size > 0 && { team_ids: { in: [...teamIds] } }),
    },
  };
}

export default function PersonnelTab({ onCheckboxChange, selections }) {
  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const { projectId } = useProjectPersonnelContext();

  const [currentSearch, setCurrentSearch] = useState('');

  // Search params are snapshotted rather than derived from the current selections
  // so that checking/unchecking personnel never changes the query key
  const [searchParams, setSearchParams] = useState(() =>
    buildSearchParams(selections),
  );

  useDebounce(currentSearch, 250, {
    onChange: ({ currentValue }) => {
      setSearchParams(
        currentValue ? { search: currentValue } : buildSearchParams(selections),
      );
    },
  });

  const [isAddPersonnelSidePanelOpen, setIsAddPersonnelSidePanelOpen] =
    useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectPersonnel.components.assignPersonnelSidePanel.components.PersonnelTab',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const {
    data: domainAssignablePersonnel,
    fetchNextPage: fetchNextDomainAssignablePersonnelPage,
    hasNextPage: hasNextDomainAssignablePersonnelPage,
    isFetching: isDomainAssignablePersonnelFetching,
    isSuccess: isDomainAssignablePersonnelSuccess,
  } = useInfiniteProjectsAssignablePersonnelSearchesQuery({
    params: searchParams,
    projectId,
    queryOptions: { keepPreviousData: true },
  });

  const handleInViewChange = (isInView) => {
    if (isInView) fetchNextDomainAssignablePersonnelPage();
  };

  const { scopedCount, totalCount } = domainAssignablePersonnel?.meta || {};

  return (
    <>
      <div className='tw-p-5'>
        <SearchField
          additionalClasses='tw-w-full'
          isDisabled={totalCount === 0}
          name='search'
          onChange={(event) => setCurrentSearch(event.target.value)}
          onReset={() => setCurrentSearch('')}
          placeholder={i18nTCommon('shared.searchByName')}
          value={currentSearch}
        />
      </div>
      {/* Fixed height needed for vertical scrolling for this section within side panel */}
      <div className='tw-flex tw-h-[calc(100vh-270px)] tw-flex-col tw-gap-3'>
        {isDomainAssignablePersonnelSuccess ?
          <p className='tw-m-0 tw-px-5 tw-font-inter tw-text-m tw-font-normal tw-tracking-auto'>
            {totalCount === 0 ?
              i18nT('results.title.noPersonnel')
            : searchParams.search ?
              i18nT('results.title.search', {
                count: scopedCount,
                interpolation: { escapeValue: false },
                search: searchParams.search,
              })
            : searchParams.filter ?
              i18nT('results.title.selections', { count: scopedCount })
            : i18nT('results.title.default', {
                count: domainAssignablePersonnel.data.length,
              })
            }
          </p>
        : <div className='tw-mx-5 tw-h-3 tw-w-50 tw-animate-pulse tw-rounded-lg tw-bg-grey-100' />
        }
        {!isDomainAssignablePersonnelSuccess || scopedCount > 0 ?
          <div className='tw-h-full tw-overflow-y-auto tw-px-5'>
            {isDomainAssignablePersonnelSuccess ?
              <>
                {domainAssignablePersonnel.data.map((personnel, index) => {
                  const {
                    attributes: { firstName, lastName },
                    id: personnelId,
                    meta: { isAlreadyAdded },
                    relationships: {
                      profilePhoto: { data: profilePhotoData },
                      teams: { data: teamsData } = { data: [] },
                      userCompanyRoles: { data: userCompanyRolesData } = {
                        data: [],
                      },
                    },
                  } = personnel;

                  const name = personDisplayName({
                    attributes: { firstName, lastName },
                  });

                  const profilePhoto =
                    profilePhotoData &&
                    domainAssignablePersonnel.included.profilePhoto[
                      profilePhotoData.id
                    ];

                  const userCompanyRoles = userCompanyRolesData.map(
                    (userCompanyRole) =>
                      domainAssignablePersonnel.included.userCompanyRole[
                        userCompanyRole.id
                      ],
                  );

                  const companyRoles = userCompanyRoles.map(
                    (userCompanyRole) =>
                      domainAssignablePersonnel.included.companyRole[
                        userCompanyRole.relationships.companyRole.data.id
                      ],
                  );

                  const isSelected = selections.personnel.has(personnelId);

                  return (
                    <div
                      className={classNames(
                        'tw-flex tw-h-18 tw-items-center tw-gap-3 tw-py-4',
                        {
                          'tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100':
                            index !== 0,
                        },
                      )}
                      key={personnelId}
                    >
                      <ConditionalWrapper
                        condition={isAlreadyAdded}
                        wrapper={(children) => (
                          <Tooltip
                            placement='bottom'
                            tooltip={i18nT('results.checkbox.disabledTooltip', {
                              interpolation: { escapeValue: false },
                              singularRAMSName,
                            })}
                            trigger='hover'
                          >
                            {children}
                          </Tooltip>
                        )}
                      >
                        <div className='tw-px-2'>
                          <Checkbox
                            isChecked={isAlreadyAdded || isSelected}
                            isDisabled={isAlreadyAdded}
                            label={i18nTCommon(
                              isSelected ?
                                'shared.deselectName'
                              : 'shared.selectName',
                              { interpolation: { escapeValue: false }, name },
                            )}
                            name='select-personnel'
                            onChange={() => {
                              onCheckboxChange({
                                personnelId,
                                roleIds: companyRoles.map(
                                  (companyRole) => companyRole.id,
                                ),
                                teamIds: teamsData.map((team) => team.id),
                              });
                            }}
                          />
                        </div>
                      </ConditionalWrapper>
                      <ProfilePhotoDisplay
                        componentSize='sm'
                        photoUrl={profilePhoto?.links?.croppedUrl}
                        profileInitials={`${firstName[0]}${lastName[0]}`}
                      />
                      <div className='tw-flex tw-flex-col tw-gap-1 tw-truncate tw-font-inter tw-tracking-auto'>
                        <p className='tw-m-0 tw-truncate tw-text-m tw-font-semibold tw-text-grey-700'>
                          {name}
                        </p>
                        {companyRoles.length > 0 && (
                          <TruncatableText
                            className='tw-m-0 tw-truncate tw-text-s tw-font-normal tw-text-grey-500'
                            text={sortRolesByPrimaryAndPosition(
                              userCompanyRoles,
                              companyRoles,
                            )
                              .map(
                                (companyRole) =>
                                  companyRole.attributes.position,
                              )
                              .join(', ')}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
                {(searchParams.search || searchParams.filter) &&
                  hasNextDomainAssignablePersonnelPage &&
                  !isDomainAssignablePersonnelFetching && (
                    <InView
                      as='div'
                      className='tw-h-1 tw-w-full'
                      onChange={handleInViewChange}
                    />
                  )}
              </>
            : <Skeleton />}
          </div>
        : (() => {
            const Icon = totalCount === 0 ? PersonIcon : SearchOffIcon;

            return (
              <div className='tw-mt-8 tw-flex tw-flex-col tw-items-center tw-px-5'>
                <Icon className='tw-h-12 tw-w-12 [&_path]:tw-fill-grey-300' />
                <p className='tw-m-0 tw-font-inter tw-text-l tw-font-semibold tw-text-grey-900'>
                  {i18nT(
                    totalCount === 0 ?
                      'results.empty.noPersonnel'
                    : 'results.empty.search',
                  )}
                </p>
                <OutlinedButton
                  className='tw-mt-8'
                  color='grey'
                  onClick={() => setIsAddPersonnelSidePanelOpen(true)}
                  size='sm'
                >
                  <div className='tw-flex tw-items-center tw-gap-3'>
                    <AddIcon height={16} width={16} />
                    {i18nTCommon('personnel.addNewPersonnel')}
                  </div>
                </OutlinedButton>
              </div>
            );
          })()
        }
      </div>
      {isAddPersonnelSidePanelOpen && (
        <AddPersonnelSidePanel
          defaultNameValue={searchParams.search || ''}
          onClose={() => setIsAddPersonnelSidePanelOpen(false)}
          onSuccess={({ personnelId, roleIds, teamIds }) => {
            onCheckboxChange({ personnelId, roleIds, teamIds });
          }}
        />
      )}
    </>
  );
}

PersonnelTab.propTypes = {
  onCheckboxChange: PropTypes.func.isRequired,
  selections: PropTypes.shape({
    personnel: PropTypes.instanceOf(Map).isRequired,
    roleIds: PropTypes.instanceOf(Set).isRequired,
    teamIds: PropTypes.instanceOf(Set).isRequired,
  }).isRequired,
};

function Skeleton() {
  return Array.from({ length: 8 }, (_, index) => (
    <div
      className={classNames(
        'tw-flex tw-h-18 tw-items-center tw-gap-3 tw-py-4',
        {
          'tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100':
            index !== 0,
        },
      )}
      key={index}
    >
      <div className='tw-mx-2 tw-h-4 tw-w-4 tw-animate-pulse tw-rounded-sm tw-bg-grey-100' />
      <div className='tw-h-8 tw-w-8 tw-animate-pulse tw-rounded-full tw-bg-grey-100' />
      <div className='tw-flex tw-flex-col tw-gap-1'>
        <div className='tw-h-3 tw-w-40 tw-animate-pulse tw-rounded-lg tw-bg-grey-100' />
        <div className='tw-h-3 tw-w-32 tw-animate-pulse tw-rounded-lg tw-bg-grey-100' />
      </div>
    </div>
  ));
}
